<script setup>
import {onMounted, ref, watch} from 'vue'
import ButtonWithConfirmation from "../buttons/buttonWithConfirmation.vue";
import TagsMultiSelect from "../crm/tags/multiSelect.vue";
import InputDatepicker from "../forms/inputDatepicker.vue";

const emit = defineEmits(['close', 'update:style'])

const props = defineProps({
    visibilityOptions: {
        type: Object,
        required: false,
        default: {
            permittingTags: [],
            prohibitingTags: [],
            visibleFrom: null,
            visibleUntil: null,
        }
    },
})

const _visibilityOptions = ref(JSON.parse(JSON.stringify(props.visibilityOptions)))
const _undoVisibilityOptions = ref(JSON.parse(JSON.stringify(props.visibilityOptions)))

const _permittingTagIds = ref([])
const _prohibitingTagIds = ref([])
const _visibleFrom = ref(null)
const _visibleUntil = ref(null)

const close = () => {
    emit('close');
}

const saveAndClose = () => {
    const options = {
        permittingTagIds: _permittingTagIds.value,
        prohibitingTagIds: _prohibitingTagIds.value,
        visibleFrom: _visibleFrom.value,
        visibleUntil: _visibleUntil.value,
    }

    emit('update:visibility-options', options)

    emit('save')
    emit('close')
}

const undoAndClose = () => {
    _visibilityOptions.value = JSON.parse(JSON.stringify(_undoVisibilityOptions.value))
    emit('update:visibility-options', _visibilityOptions.value)
    emit('close')
}

const onPermittingTagsSelected = (e) => {
    _permittingTagIds.value = e.map(tag => tag.id)
}

const onProhibitingTagsSelected = (e) => {
    _prohibitingTagIds.value = e.map(tag => tag.id)
}

const onVisibleFromUpdated = (event) => {
    _visibleFrom.value = event
}

const onVisibleUntilUpdated = (event) => {
    _visibleUntil.value = event
}

watch(props.visibilityOptions, () => {
    _permittingTagIds.value = props.visibilityOptions?.permittingTagIds || []
    _prohibitingTagIds.value = props.visibilityOptions?.prohibitingTagIds || []
    _visibleFrom.value = props.visibilityOptions?.visibleFrom || null
    _visibleUntil.value = props.visibilityOptions?.visibleUntil || null
})

onMounted(() => {
    _permittingTagIds.value = props.visibilityOptions?.permittingTagIds || []
    _prohibitingTagIds.value = props.visibilityOptions?.prohibitingTagIds || []
    _visibleFrom.value = props.visibilityOptions?.visibleFrom || null
    _visibleUntil.value = props.visibilityOptions?.visibleUntil || null
})

</script>

<template>
    <slideout-with-slots
        @close="close"
    >
        <template #title>
            <h2>{{ _mft('pagebuilder:visibilityEditor.title') }}</h2>
        </template>
        <template #actions>
            <div class="mb-2 flex justify-end space-x-2">
                <button-with-confirmation
                    @confirmed="undoAndClose"
                    modalClass="absolute -top-2 -right-2 p-2 bg-white rounded shadow-md z-10 text-black"
                    :buttonLabel="_mft('shared:cancel')"
                    buttonClass="text-wlPrimaryContrast"
                    :confirmButtonLabel="_mft('shared:confirm.continue')"
                    confirmButtonClass="myflow-basic-button--success whitespace-nowrap"
                    :confirm-text="_mft('pagebuilder:styleEditor.confirm.undoChanges')"
                ></button-with-confirmation>

                <button-success
                    @click="saveAndClose"
                >{{ _mft('shared:action.save') }}
                </button-success>
            </div>
        </template>
        <template #body>
            <div class="pb-24">
                <div>
                    <input-label :label="'Show only if user has one or more of these tags'" />
                    <tags-multi-select-v2
                        :pre-selected-tags="_permittingTagIds"
                        :allow-creation="false"
                        :select-multiple="true"
                        :placeholder="_mft('crm:searchTags')"
                        @tagsSelected="onPermittingTagsSelected"
                    />
                </div>

                <div>
                    <input-label :label="'Hide row if user has one or more of these tags'" />
                    <tags-multi-select-v2
                        :pre-selected-tags="_prohibitingTagIds"
                        :allow-creation="false"
                        :select-multiple="true"
                        :placeholder="_mft('crm:searchTags')"
                        @tagsSelected="onProhibitingTagsSelected"
                    />
                </div>

                <div>
                    <input-label :label="'Visible from'" />
                    <div class="flex space-x-2">
                        <input-datepicker
                            @update:date="onVisibleFromUpdated"
                            :date="_visibleFrom"
                            :rangeMode="false"
                            :placeholder="_visibleFrom === null ? 'Always visible' : ''"
                            :show-time="true"
                            :show-date="true"
                            :show-inline="false"
                        />
                        <button-danger
                            v-if="_visibleFrom !== null"
                            size="sm"
                            @click="_visibleFrom = null"
                        >
                            {{ _mft('shared:action.clear') }}
                        </button-danger>
                    </div>
                    <div class="text-xxs text-gray-400">Visible from has to be in the future.</div>
                </div>

                <div class="mt-4">
                    <input-label :label="'Visible until'" />
                    <div class="flex space-x-2">
                        <input-datepicker
                            @update:date="onVisibleUntilUpdated"
                            :date="_visibleUntil"
                            :rangeMode="false"
                            :placeholder="_visibleUntil === null ? 'Always visible' : ''"
                            :show-time="true"
                            :show-date="true"
                            :show-inline="false"
                        />
                        <button-danger
                            v-if="_visibleUntil !== null"
                            size="sm"
                            @click="_visibleUntil = null"
                        >
                            {{ _mft('shared:action.clear') }}
                        </button-danger>
                    </div>
                    <div class="text-xxs text-gray-400">Visible until has to be in the future.</div>
                </div>
            </div>
        </template>
    </slideout-with-slots>
</template>
